// Error handling UI shared by the admin and the console: a screen that crashes while rendering shows
// a panel with Try again instead of a blank page, and the rest of the app (sidebar, top bar) keeps
// working. A code-split screen whose file is gone after a deploy (the PWA still runs the old build)
// asks for a reload instead, since retrying would fail the same way.
import { Component, type ErrorInfo, type ReactNode } from "react";
import { t } from "@ady/i18n";
import { ErrorState } from "./StatePanel";

/** A lazily loaded file that no longer exists (a newer version was deployed). */
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name} ${err.message}` : String(err);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError|Unable to preload CSS/i.test(
    msg,
  );
}

/** The panel for an error caught while rendering (also the router's error screen). */
export function CrashPanel({
  error,
  onRetry,
  size = "card",
}: {
  error: unknown;
  onRetry?: () => void;
  size?: "page" | "card";
}) {
  if (isChunkLoadError(error))
    return (
      <ErrorState
        size={size}
        title={t("states.updatedTitle")}
        body={t("states.updatedBody")}
        onRetry={() => location.reload()}
      />
    );
  return (
    <ErrorState
      size={size}
      title={t("states.crashTitle")}
      body={t("states.crashBody")}
      onRetry={onRetry}
    />
  );
}

interface Props {
  children: ReactNode;
  /** A new value (the page path) clears the error, so moving to another page recovers. */
  resetKey?: unknown;
  size?: "page" | "card";
}

export class ErrorBoundary extends Component<Props, { error: unknown }> {
  override state: { error: unknown } = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error(
      JSON.stringify({
        level: "error",
        msg: "ui_crash",
        error: String(error).slice(0, 300),
        stack: info.componentStack?.slice(0, 600),
      }),
    );
  }

  override componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  override render() {
    if (this.state.error)
      return (
        <CrashPanel
          error={this.state.error}
          size={this.props.size}
          onRetry={() => this.setState({ error: null })}
        />
      );
    return this.props.children;
  }
}
